<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-cinza-900/95"
    @click.self="$emit('close')"
    role="dialog"
    aria-modal="true"
    :aria-label="t('vitrine.carousel.galleryAria')"
  >
    <!-- Fechar -->
    <button
      class="absolute top-4 right-4 p-2 rounded-full text-branco hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado"
      :aria-label="t('vitrine.carousel.closeAria')"
      @click="$emit('close')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>

    <!-- Zoom e contador -->
    <div
      class="absolute top-4 left-4 flex gap-2 items-center text-branco"
      :aria-label="t('vitrine.carousel.zoomAria')"
    >
      <button
        class="p-2 rounded-full hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado"
        :aria-label="t('vitrine.carousel.zoomIn')"
        @click="zoomIn"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0
               0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
          />
        </svg>
      </button>
      <button
        class="p-2 rounded-full hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado"
        :aria-label="t('vitrine.carousel.zoomOut')"
        @click="zoomOut"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0
               0114 0zM13 10H7"
          />
        </svg>
      </button>
      <span class="ml-2 text-small hidden sm:inline">
        {{ counterText }}
      </span>
    </div>

    <!-- Área da imagem -->
    <div
      class="relative max-w-[95vw] max-h-[75vh] md:max-h-[80vh] flex items-center justify-center overflow-hidden"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @pointerleave="onPointerCancel"
    >
      <transition name="fade">
        <img
          :key="currentImage"
          :src="currentImage"
          :alt="currentAlt"
          class="rounded shadow-lg select-none transform transition-transform duration-200 cursor-zoom-in"
          :class="zoomClass"
          :style="zoomStyle"
          @dblclick="toggleZoom"
          @load="onLoad"
          @error="onError"
          draggable="false"
        />
      </transition>

      <!-- Prev sobre a imagem (sm-) -->
      <button
        class="absolute top-1/2 -translate-y-1/2 left-2 p-3 rounded-full text-branco bg-cinza-900/30 z-20 hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado md:hidden"
        :aria-label="t('vitrine.carousel.prev')"
        @click="$emit('prev')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      <!-- Next sobre a imagem (sm-) -->
      <button
        class="absolute top-1/2 -translate-y-1/2 right-2 p-3 rounded-full text-branco bg-cinza-900/30 z-20 hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado md:hidden"
        :aria-label="t('vitrine.carousel.next')"
        @click="$emit('next')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
    </div>

    <!-- Prev nas laterais (md+) -->
    <button
      class="hidden md:flex absolute left-3 md:left-6 top-1/2 -translate-y-1/2 p-3 rounded-full text-branco hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado z-30"
      :aria-label="t('vitrine.carousel.prev')"
      @click="$emit('prev')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>

    <!-- Next nas laterais (md+) -->
    <button
      class="hidden md:flex absolute right-3 md:right-6 top-1/2 -translate-y-1/2 p-3 rounded-full text-branco hover:text-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado z-30"
      :aria-label="t('vitrine.carousel.next')"
      @click="$emit('next')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-8 w-8"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Thumbnails -->
    <div
      v-if="images && images.length > 1"
      class="absolute bottom-4 left-0 right-0 px-4 md:px-8"
    >
      <div
        class="mx-auto max-w-5xl flex gap-2 overflow-x-auto py-2 justify-center"
      >
        <button
          v-for="(img, idx) in images"
          :key="`${idx}-${img}`"
          class="relative flex-shrink-0 w-16 h-16 rounded overflow-hidden ring-2 transition hover:ring-amarelo-dourado focus:outline-none focus:ring-2 focus:ring-amarelo-dourado"
          :class="{ 'ring-azul-profundo': idx === safeIndex }"
          @click="goTo(idx)"
          :aria-label="t('vitrine.carousel.preview', { i: idx + 1 })"
        >
          <img
            :src="img"
            alt=""
            class="w-full h-full object-cover"
            draggable="false"
          />
        </button>
      </div>
    </div>

    <!-- Sem imagens -->
    <div
      v-if="!hasImages"
      class="absolute inset-0 flex items-center justify-center text-branco px-6 text-center"
      role="alert"
      aria-live="polite"
    >
      <p class="bg-cinza-900/60 rounded px-4 py-3">
        {{ t("vitrine.carousel.noPhotos") }}
      </p>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";

export default {
  name: "VitrineFullscreenCarousel",
  inject: ["psImageAlt"],
  props: {
    images: { type: Array, default: () => [] },
    activeIndex: { type: Number, default: 0 },
    product: { type: Object, default: null },
  },
  emits: ["close", "prev", "next"],
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      // Zoom simples
      zoom: 1,
      // Fallback de imagem
      lastGoodSrc: "",
      // Gestos: arrastar (Pointer Events)
      isDragging: false,
      dragStartX: 0,
      dragDeltaX: 0,
      // Threshold para navegação
      dragThreshold: 40,
    };
  },
  computed: {
    hasImages() {
      return Array.isArray(this.images) && this.images.length > 0;
    },
    safeIndex() {
      if (!this.hasImages) return 0;
      const i = Number.isFinite(this.activeIndex) ? this.activeIndex : 0;
      return Math.min(Math.max(i, 0), this.images.length - 1);
    },
    currentImage() {
      if (!this.hasImages) return "";
      return this.images[this.safeIndex];
    },
    currentAlt() {
      if (this.psImageAlt && this.product) {
        return this.psImageAlt(this.product, this.safeIndex);
      }
      return this.t("vitrine.carousel.alt");
    },
    zoomClass() {
      if (this.zoom <= 1) return "scale-100";
      if (this.zoom < 1.5) return "scale-110 md:scale-125";
      return "scale-125 md:scale-150";
    },
    zoomStyle() {
      return this.zoom > 1 ? { transform: `scale(${this.zoom})` } : null;
    },
    counterText() {
      if (!this.hasImages) return "0 / 0";
      return `${this.safeIndex + 1} / ${this.images.length}`;
    },
  },
  mounted() {
    // Teclado: ← → Esc
    window.addEventListener("keydown", this.onKey);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKey);
  },
  methods: {
    // Navegação por teclado
    onKey(e) {
      if (e.key === "ArrowLeft") this.$emit("prev");
      if (e.key === "ArrowRight") this.$emit("next");
      if (e.key === "Escape") this.$emit("close");
    },

    // Thumbnails
    goTo(targetIdx) {
      if (!this.hasImages) return;
      const n = this.images.length;
      if (targetIdx === this.safeIndex) return;
      const forward = (targetIdx - this.safeIndex + n) % n;
      const backward = (this.safeIndex - targetIdx + n) % n;
      const fw = forward <= backward ? forward : 0;
      const bw = forward <= backward ? 0 : backward;
      for (let i = 0; i < fw; i += 1) this.$emit("next");
      for (let i = 0; i < bw; i += 1) this.$emit("prev");
    },

    // Zoom
    zoomIn() {
      this.zoom = Math.min(this.zoom + 0.25, 2);
    },
    zoomOut() {
      this.zoom = Math.max(this.zoom - 0.25, 1);
    },
    toggleZoom() {
      this.zoom = this.zoom > 1 ? 1 : 2;
    },

    // Eventos de imagem
    onLoad(e) {
      this.lastGoodSrc = e?.target?.currentSrc || this.currentImage;
    },
    onError() {
      // Parent controla índice. Mantém estado atual.
    },

    // Gesto de arrastar
    onPointerDown(e) {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragDeltaX = 0;
    },
    onPointerMove(e) {
      if (!this.isDragging) return;
      this.dragDeltaX = e.clientX - this.dragStartX;
    },
    onPointerUp(e) {
      if (e?.currentTarget?.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      if (!this.isDragging) return;
      const dx = this.dragDeltaX;
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;
      if (Math.abs(dx) < this.dragThreshold) return;
      if (dx < 0) this.$emit("next");
      else this.$emit("prev");
    },
    onPointerCancel(e) {
      if (e?.currentTarget?.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;
    },
  },
};
</script>

<style scoped>
/* Transição de opacidade na troca de imagens */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
